/**
 * LandingTrending — public "momentum leaderboard" strip on the marketing Landing.
 *
 * Uses `/api/clans/trending?limit=6` (public, no auth) — the same GL4 endpoint
 * used inside HomeFeed but rendered with a Landing-native visual treatment
 * (grid, big cards, single per-card CTA that jumps into the clan feed → this
 * bounces unauthed visitors into the invite/waitlist flow via existing
 * auth guards).
 *
 * Hides itself entirely when the endpoint returns zero clans (fresh app or
 * no non-demo creators with real momentum yet). Never shows an empty state
 * on Landing — silence is better than "no data" on a marketing page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveAvatarUrl } from '../api';

const fmtFollowers = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return String(v);
};

const platformIcon = (p) => (p === 'youtube' ? '▶' : '𝕏');

const CATEGORY_LABEL = {
  fitness: 'Fitness', gaming: 'Gaming', music: 'Music', comedy: 'Comedy',
  art: 'Art', tech: 'Tech', education: 'Education', lifestyle: 'Lifestyle',
  food: 'Food', fashion: 'Fashion', travel: 'Travel',
};

export default function LandingTrending() {
  const navigate = useNavigate();
  const [clans, setClans] = useState(null); // null = loading, [] = empty, [...] = has data

  useEffect(() => {
    let cancelled = false;
    api.get('/clans/trending?limit=6')
      .then(({ data }) => { if (!cancelled) setClans(data?.clans || []); })
      .catch(() => { if (!cancelled) setClans([]); });
    return () => { cancelled = true; };
  }, []);

  // Silent while loading + when no data — Landing must never look broken.
  if (!clans || clans.length === 0) return null;

  return (
    <section className="landing-trending" data-testid="landing-trending">
      <h2 className="landing-h2">
        Creators on the rise <span aria-hidden="true">🚀</span>
      </h2>
      <p className="landing-trending-sub">
        Live momentum from X &amp; YouTube. These clans are growing fastest today.
      </p>

      <div className="landing-trending-grid">
        {clans.map((c, idx) => {
          const rank = idx + 1;
          const grad = Array.isArray(c.gradient) && c.gradient.length >= 2
            ? c.gradient : ['#F5A623', '#F5C244'];
          const avatarSrc = c.avatar ? resolveAvatarUrl(c.avatar) : null;
          const cat = c.category ? (CATEGORY_LABEL[c.category] || c.category) : null;
          const pct24 = Number(c.pct_24h) || 0;
          const pctSign = pct24 > 0 ? '+' : '';
          return (
            <button
              key={c.handle}
              type="button"
              className="landing-trending-card"
              onClick={() => navigate(`/clan/${c.handle}/feed`, { state: { from: '/landing' } })}
              data-testid={`landing-trending-card-${c.handle}`}
              aria-label={`Explore ${c.display_name}'s clan`}
            >
              <span className="landing-trending-rank" aria-hidden="true">#{rank}</span>

              <div
                className="landing-trending-avatar"
                style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <span className="landing-trending-initial" aria-hidden="true">
                    {(c.display_name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="landing-trending-meta">
                <div className="landing-trending-name">
                  {c.display_name}
                  {c.verified && <span className="landing-trending-verified" aria-label="verified">✓</span>}
                </div>
                {cat && <div className="landing-trending-cat">{cat}</div>}
              </div>

              <div className="landing-trending-stats">
                <span className="landing-trending-followers" title={`${c.followers.toLocaleString()} ${c.best_platform === 'youtube' ? 'subscribers' : 'followers'}`}>
                  {platformIcon(c.best_platform)} {fmtFollowers(c.followers)}
                </span>
                {pct24 !== 0 && (
                  <span className={`landing-trending-pct ${pct24 > 0 ? 'up' : 'down'}`}>
                    {pctSign}{pct24.toFixed(1)}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
